<template>
  <section class="shared-section">
    <header class="shared-section-header">
      <h2 class="pl-4 shared-section-header-heading sm:pl-0">Training Schedule</h2>
    </header>

    <Calendar :items="timeSlots" @update="handleUpdate" @view="handleClickTimeSlot">
      <template v-slot:header>
        <button
          @click.prevent="showAddModal = true"
          class="h-12 px-8 ml-auto button"
          :disabled="!$canI.create"
        >
          <VSvgIcon name="plus" /> Add Training
        </button>
      </template>
    </Calendar>

    <!-- Modals -->
    <TrainingModalAdd
      v-if="showAddModal"
      :show="showAddModal"
      @closeAddModal="showAddModal = false"
      @addTraining="handleAddTraining"
    />

    <TrainingModalView
      v-if="showViewModal"
      :show="showViewModal"
      :timeSlot="timeSlot"
      :appointments="appointments"
      :loading="loading"
      @closeViewModal="handleCloseViewModal"
      @addAppointment="handleAddAppointment"
      @cancelAppointment="handleCancelAppointment"
      @attendedAppointment="handleAttendedAppointment"
      @reverseAttendedAppointment="handleReverseAttendedAppointment"
      @deleteTimeSlot="handleDeleteTimeSlot"
      @updateTrainingSlot="handleUpdateTrainingSlot"
    />
    <Loading :show="loading" :porcentage="porcentage_loading" />
  </section>
</template>

<script>
import { format, addHours } from 'date-fns';

import { formatDateTimeZone, typeCheck } from '@/utils';

import VSvgIcon from '@/components/base/VSvgIcon';
import Calendar from '@/components/shared/calendar/Calendar';
import Loading from '@/components/shared/Loading';
import TrainingModalAdd from '@/components/admin/TrainingModalAdd';
import TrainingModalView from '@/components/admin/TrainingModalView';

export default {
  name: 'AdminTrainingSchedule',
  components: {
    TrainingModalAdd,
    TrainingModalView,
    VSvgIcon,
    Calendar,
    Loading,
  },
  watch: {
    chunks(n) {
      if (n.length > 0) {
        this.upload();
      }
    },
  },
  data() {
    return {
      showAddModal: false,
      showViewModal: false,
      timeSlots: [],
      timeSlot: null,
      appointments: [],
      file: '',
      porcentage_loading: 0,
      chunks_original_lenght: 0,
      loading: false,
      dataForm: {},
      chunks: [],
    };
  },
  computed: {
    progress() {
      return Math.floor((this.uploaded * 100) / this.file.size);
    },
    formData() {
      const formData = this.setFormData(this.dataForm);
      formData.set('location', this.$route.params.location);

      if (this.file && typeCheck(this.file).isFile) {
        formData.set('is_last', this.chunks.length === 1);
        formData.set('video_record_url', this.chunks[0], `${this.file.name}.part`);
      }
      return formData;
    },
  },
  methods: {
    async upload() {
      const { data } = await this.$http.post(
        `/admin/training-scheduling/${this.timeSlot.id}`,
        this.formData,
      );
      this.chunks.shift();
      this.porcentage_loading = (
        ((this.chunks_original_lenght - this.chunks.length) / this.chunks_original_lenght) *
        100
      ).toFixed();
      this.timeSlots = this.timeSlots.map(t =>
        t.id === data.data.id ? this.prepTimeSlot(data.data) : t,
      );
      this.chunks.length === 0 && this.handleCloseViewModal();
    },
    createChunks() {
      let size = 10000000,
        chunks = Math.ceil(this.file.size / size);

      for (let i = 0; i < chunks; i++) {
        this.chunks.push(
          this.file.slice(i * size, Math.min(i * size + size, this.file.size), this.file.type),
        );
      }
      this.chunks_original_lenght = this.chunks.length;
    },
    setFormData(data) {
      const reducer = (acc, [key, value]) => {
        if (typeCheck(value).isNum || value) return acc.append(key, value), acc;
        return acc;
      };

      return Object.entries(data).reduce(reducer, new FormData());
    },
    async handleUpdateTrainingSlot(formTrainingSlot) {
      try {
        const {
          maxPeople,
          trainingTopic,
          classNote,
          classLink,
          videoRecordUrl,
          status,
          ...rest
        } = formTrainingSlot;
        this.dataForm = {
          ...rest,
          max_people: maxPeople,
          training_topic: trainingTopic,
          class_note: classNote,
          class_link: classLink,
          status: status,
        };
        this.file = videoRecordUrl;
        this.loading = true;
        typeCheck(this.file).isFile ? this.createChunks() : this.upload();
      } catch (error) {
        console.error({ ...error });
      }
    },
    handleCloseViewModal() {
      this.showViewModal = false;
      this.loading = false;
      this.timeSlot = null;
      this.chunks_original_lenght = 0;
      this.porcentage_loading = 0;
    },
    async handleAddTraining(data) {
      try {
        const newData = { ...data, location: this.$route.params.location };
        const res = await this.$http.post('/admin/training-scheduling', newData);
        if (res.data.data.length) {
          this.timeSlots = [...this.timeSlots, ...this.prepTimeSlotData(res.data.data)];
        }
      } catch (error) {
        console.error({ ...error });
      }
    },
    async handleUpdate(start, end) {
      this.timeSlots = await this.fetchTimeSlots(start, end);
    },
    async handleClickTimeSlot(item) {
      try {
        this.timeSlot = item;
        this.showViewModal = true;
        const { data } = await this.$http.get('/admin/training-scheduling/appointments', {
          params: { training_slot_id: item.id },
        });
        this.appointments = data.data;
      } catch (error) {
        console.error({ ...error });
      }
    },
    async handleCancelAppointment(appointmentId) {
      try {
        await this.$http.delete(`/admin/training-scheduling/appointments/${appointmentId}`);
        this.appointments = this.appointments.filter(({ id }) => appointmentId !== id);
      } catch (error) {
        console.error({ ...error });
      }
    },
    async handleAttendedAppointment(appointmentId) {
      try {
        const { data } = await this.$http.put(
          `/admin/training-scheduling/appointments/${appointmentId}`,
        );
        this.appointments = this.appointments.map(appointment => {
          if (appointmentId === appointment.id) appointment.attended = data.attended;
          return appointment;
        });
      } catch (error) {
        console.error({ ...error });
      }
    },
    async handleReverseAttendedAppointment(appointmentId) {
      try {
        const { data } = await this.$http.put(
          `/admin/training-scheduling/reverse-appointments/${appointmentId}`,
        );
        this.appointments = this.appointments.map(appointment => {
          if (appointmentId === appointment.id) appointment.attended = data.attended;
          return appointment;
        });
      } catch (error) {
        console.error({ ...error });
      }
    },
    async handleAddAppointment(timeSlotId, contactId, sendEmail) {
      try {
        await this.$http.post(`/admin/training-scheduling/add-appoinment`, {
          timeSlotId,
          contactId,
          sendEmail,
        });
        const { data } = await this.$http.get('/admin/training-scheduling/appointments', {
          params: { training_slot_id: timeSlotId },
        });
        this.appointments = data.data;
      } catch (error) {
        console.error({ ...error });
      }
    },
    async handleDeleteTimeSlot(timeSlotId) {
      try {
        await this.$http.delete(`/admin/training-scheduling/${timeSlotId}`);
        this.timeSlots = this.timeSlots.filter(({ id }) => timeSlotId !== id);
        this.handleCloseViewModal();
      } catch (error) {
        console.error(error);
      }
    },
    async fetchTimeSlots(start, end) {
      try {
        const from = format(new Date(start), 'yyyy-MM-dd');
        const to = format(new Date(end), 'yyyy-MM-dd');
        const location = this.$route.params.location;

        const { data } = await this.$http.get('/admin/training-scheduling/time-slots', {
          params: { from, to, location },
        });

        return this.prepTimeSlotData(data.data);
      } catch (error) {
        console.error(error);
      }
    },
    prepTimeSlot(timeSlot) {
      // Machine Series
      // CHS2, CHT, CHT2, EM, FHA, FHT, MCC, MT, PV, RCM, SWD, Chroma
      const colors = {
        CHS2: { color: 'rgb(50, 168, 82)', bg: 'rgba(50, 168, 82, 0.2)' },
        CHT: { color: 'rgb(50, 56, 168)', bg: 'rgba(50, 56, 168, 0.2)' },
        CHT2: { color: 'rgb(224, 156, 38)', bg: 'rgba(224, 156, 38, 0.2)' },
        EM: { color: 'rgb(209, 61, 59)', bg: 'rgba(209, 61, 59, 0.2)' },
        FHA: { color: 'rgb(219, 198, 37)', bg: 'rgba(219, 198, 37, 0.2)' },
        FHT: { color: 'rgb(189, 48, 201)', bg: 'rgba(189, 48, 201, 0.2)' },
        MCC: { color: 'rgb(122, 48, 201)', bg: 'rgba(122, 48, 201, 0.2)' },
        MT: { color: 'rgb(48, 201, 186)', bg: 'rgba(48, 201, 186, 0.2)' },
        PV: { color: 'rgb(48, 201, 188)', bg: 'rgba(48, 201, 188, 0.2)' },
        RCM: { color: 'rgb(71, 201, 184)', bg: 'rgba(71, 201, 184, 0.2)' },
        SWD: { color: 'rgb(146, 56, 201)', bg: 'rgba(146, 56, 201, 0.2)' },
        CHROMA: { color: 'rgb(52, 133, 191)', bg: 'rgba(52, 133, 191, 0.2)' },
        MARQUEE: { color: 'rgb(139, 92, 246)', bg: 'rgba(139, 92, 246, 0.2)' },
        DEFAULT: { color: 'rgb(52, 133, 191)', bg: 'rgba(52, 133, 191, 0.2)' },
        DRAFT: { color: 'rgb(105, 105, 105)', bg: 'rgba(169, 169, 169, 0.4)' },
      };

      const { id, time, type_id, lang_code, product_groups, max_people, seats_taken } = timeSlot;

      const product = product_groups[0]['name'].split(' ')[0];
      const trainingType = `${type_id.slice(0, 1).toUpperCase()}${type_id.slice(1)}`;
      const language = lang_code.toUpperCase();
      const trainingTime = formatDateTimeZone(time);

      const maxCapacity = max_people;
      const occupiedCapacity = seats_taken;

      const startDate = new Date(time);
      const endDate = addHours(new Date(time), 1);

      const title = `<span class="font-bold">${product}</span>: ${trainingType}, ${language}, ${trainingTime}`;

      const color =
        timeSlot.status === 'publish'
          ? colors[product.toUpperCase()] ?? colors['DEFAULT']
          : colors['DRAFT'];

      return {
        id,
        startDate,
        endDate,
        maxCapacity,
        occupiedCapacity,
        title,
        style: {
          backgroundColor: `${color['bg']}`,
          borderColor: `${color['color']}`,
          color: `${color['color']}`,
        },
        ...timeSlot,
      };
    },
    prepTimeSlotData(data) {
      return data.map(this.prepTimeSlot);
    },
  },
};
</script>
